import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, Typography, Switch, FormControlLabel, Box, Button, CircularProgress, Tabs, Tab } from '@mui/material';
import { handleUnauthorized } from '../../TokenAuth/auth';

function ShowDeleteAccountButton() {
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [userState, setUserState] = useState({ android: false, ios: false });
    const [astroState, setAstroState] = useState({ android: false, ios: false });

    const token = localStorage.getItem("astroVani-admin-token");
    const baseUrl = process.env.REACT_APP_BASEURL;

    useEffect(() => {
        fetchStatus('user');
        fetchStatus('astro');
    }, []);

    const fetchStatus = async (type) => {
        try {
            setLoading(true);
            const endpoint = type === 'user' ? '/admin/userAccountButton' : '/admin/astroAccountButton';
            const response = await axios.get(`${baseUrl}${endpoint}`, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
            });

            if (response.data.success) {
                const value = response.data.data?.value || { android: false, ios: false };
                if (type === 'user') setUserState(value);
                else setAstroState(value);
            } else {
                toast.error(response.data.message || `Failed to fetch ${type} status`);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error(`Error fetching ${type} status:`, error);
                toast.error(error.response?.data?.message || `Error fetching ${type} status`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleChange = (type, platform) => (event) => {
        const checked = event.target.checked;
        if (type === 'user') {
            setUserState(prev => ({ ...prev, [platform]: checked }));
        } else {
            setAstroState(prev => ({ ...prev, [platform]: checked }));
        }
    };

    const handleSave = async (type) => {
        try {
            setSaving(true);
            const endpoint = type === 'user' ? '/admin/userAccountButton' : '/admin/astroAccountButton';
            const payload = { value: type === 'user' ? userState : astroState };

            const response = await axios.patch(`${baseUrl}${endpoint}`, payload, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
            });

            if (response.data.success) {
                toast.success(response.data.message || 'Status updated successfully');
            } else {
                toast.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error(`Error updating ${type} status:`, error);
                toast.error(error.response?.data?.message || 'Error updating status');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom-0">
                <h4 className="mb-0 fw-bold">Delete Account Button Management</h4>
            </div>

            <div className="card-body">
                <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: 3, px: 4 }}>
                        <Tabs value={tabIndex} onChange={handleTabChange} aria-label="account button tabs">
                            <Tab label="User App" />
                            <Tab label="Astrologer App" />
                        </Tabs>
                    </Box>

                    <CardContent sx={{ p: 4 }}>
                        {tabIndex === 0 && (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                    User App Visibility Controls
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={userState.android}
                                                onChange={handleToggleChange('user', 'android')}
                                                color="primary"
                                            />
                                        }
                                        label="Android"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={userState.ios}
                                                onChange={handleToggleChange('user', 'ios')}
                                                color="primary"
                                            />
                                        }
                                        label="iOS"
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSave('user')}
                                        disabled={saving}
                                    >
                                        {saving ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {tabIndex === 1 && (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                    Astrologer App Visibility Controls
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={astroState.android}
                                                onChange={handleToggleChange('astro', 'android')}
                                                color="primary"
                                            />
                                        }
                                        label="Android"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={astroState.ios}
                                                onChange={handleToggleChange('astro', 'ios')}
                                                color="primary"
                                            />
                                        }
                                        label="iOS"
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSave('astro')}
                                        disabled={saving}
                                    >
                                        {saving ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}

export default ShowDeleteAccountButton;
